import type { TFunction } from "i18next";
import { Stack, Text, VisuallyHidden } from "@mantine/core";
import { formatDateTime, formatDuration } from "../time/format";
import { Artwork } from "../ui/Artwork";
import { FeedbackLine } from "../ui/FeedbackLine";
import type { Layout } from "../ui/useLayout";

/**
 * One sentence for what the player is doing, for anyone who cannot see the page.
 *
 * The visual answer is spread over four things -- the artwork, the title, a LIVE word beside
 * the timestamp, and the timeline's own pill -- and none of them announces itself when it
 * changes. This is the single canonical claim, and it is the reason the rest are allowed to
 * stay decorative.
 */
export function playbackStatus(
  state: {
    kind: "live" | "segment" | "window" | null;
    title: string;
    playing: boolean;
    loading: boolean;
    muted: boolean;
    error: string | null;
  },
  t: TFunction,
): string {
  // The error first: it is why nothing is playing, and reporting "Stopped" over the top of it
  // would be technically true and useless.
  if (state.error) return state.error;
  if (state.kind === null) return t("player.statusStopped");
  if (state.loading)
    return state.kind === "live"
      ? t("player.statusLoadingLive")
      : t("player.statusLoadingTitle", { title: state.title });

  // One key per whole sentence rather than a verb glued to a subject: the word order, the
  // comma and the case of "live stream" are the translation's business, not this function's.
  // Mute is a property of this page rather than of the source, so it doubles the set instead
  // of replacing it -- "paused" and "muted" are different answers to different questions and
  // both can be true. The eight keys: player.status{Playing,Paused}{Live,Title}{,Muted}.
  const verb = state.playing ? "Playing" : "Paused";
  const what = state.kind === "live" ? "Live" : "Title";
  return t(`player.status${verb}${what}${state.muted ? "Muted" : ""}`, { title: state.title });
}

export interface NowPlayingProps {
  review: boolean;
  layout: Layout;
  t: TFunction;
  title: string;
  subtitle: string | null;
  artworkUrl: string | null;
  /** A row whose audio has expired still names itself; the sleeve is what says it is gone. */
  dimmed: boolean;
  playheadMs: number;
  zoneName: string;
  durationSeconds: number | null;
  onAir: boolean;
  error: string | null;
  sourceKind: "live" | "segment" | "window" | null;
  playing: boolean;
  loading: boolean;
  muted: boolean;
}

/** The heading, the sleeve, the two caption lines, and the one sentence a reader is told. */
export function NowPlaying({
  review,
  layout,
  t,
  title,
  subtitle,
  artworkUrl,
  dimmed,
  playheadMs,
  zoneName,
  durationSeconds,
  onAir,
  error,
  sourceKind,
  playing,
  loading,
  muted,
}: NowPlayingProps) {
  return (
    <>
      {/* The label runs the width of its card, as in the reference: the desktop column
          centres its children and the phone layout is a two-column grid, and one pair of
          declarations covers both because each mode ignores the other's. */}
      <Text
        component="h2"
        size="xs"
        fw={600}
        lts="0.35px"
        tt="uppercase"
        c="dimmed"
        style={{ alignSelf: "stretch", gridColumn: "1 / -1" }}
      >
        {review ? t("player.headingReview") : t("player.heading")}
      </Text>
      {/* The cover is the one child of this card that can give up height and the only one
          that should -- a smaller cover still says which show this is, where a clipped
          transport stops working. In landscape there is no height to give: it goes.

          It fills the card rather than holding a width of its own: a cover that stays 260
          wide inside a column that grew is not a smaller cover, it is a card with a hole in
          it. The cap is what stops a very wide window turning the sleeve into the page. */}
      {!layout.landscape && (
        <Artwork
          src={artworkUrl}
          seed={title}
          size={layout.phone ? 48 : "min(100cqw, 420px)"}
          dimmed={dimmed}
        />
      )}

      <Stack gap={2} w="100%" miw={0} ta={layout.phone ? "left" : "center"}>
        <Text component="h2" fz={16} fw={700} truncate title={title}>
          {title}
        </Text>
        {subtitle && (
          <Text inherit mt={2} c="dimmed">
            {subtitle}
          </Text>
        )}
        <Text mt={6} fz={12} c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatDateTime(playheadMs, zoneName)}
          {durationSeconds !== null && (
            <Text component="span" inherit>{` · ${formatDuration(durationSeconds)}`}</Text>
          )}
          {onAir && (
            <Text component="span" inherit ml={8} c="danger" fw={700} lts="0.1em">
              {t("player.livePill")}
            </Text>
          )}
        </Text>
        <FeedbackLine mt={8} fz={12}>
          {error}
        </FeedbackLine>
      </Stack>

      {/* polite, not assertive: playback state changes because the listener asked it to, so
          there is nothing here worth interrupting whatever they are reading. */}
      <VisuallyHidden role="status" aria-live="polite">
        {playbackStatus(
          {
            kind: sourceKind,
            title,
            playing,
            loading,
            muted,
            error,
          },
          t,
        )}
      </VisuallyHidden>
    </>
  );
}
